import {
	Box,
	Button,
	FormControl,
	InputLabel,
	MenuItem,
	Select,
	SelectChangeEvent,
	TextField,
	Typography,
	Grid,
	Chip,
	Checkbox,
	ListItemText,
	OutlinedInput,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
} from '@mui/material'
import {
	GridColDef,
	GridRowSelectionModel,
	useGridApiRef,
} from '@mui/x-data-grid-premium'
import {
	GridContainer,
	PageContainer,
	PageTop,
	SearchContainer,
	StyledDataGrid,
} from 'components/ApplicationStyles'
import PagePosition from 'components/PagePosition'
import { useEffect, useState } from 'react'
import dayjs, { Dayjs } from 'dayjs'
import { koKR } from '@mui/x-data-grid-premium/locales'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import moment from 'moment'
import 'moment/locale/ko'
import UserData from './UserData'
import {
	HealthDataRequestType,
	HealthDataSearchType,
	HealthDataType,
} from 'constants/appType'
import { SpRequestType } from 'constants/types'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
	apiHealthDataList,
	apiHealthDataSave,
	apiHealthDataSummaryDetail,
	apiHealthDataSummaryList,
} from 'api/api'
import ZoomInIcon from '@mui/icons-material/ZoomIn'

// React Query 키 팩토리
const healthDataKeys = {
	all: ['healthDataList'] as const,
	lists: () => [...healthDataKeys.all, 'list'] as const,
	list: (params: HealthDataSearchType) =>
		[...healthDataKeys.lists(), params] as const,
}

type SearchType = {
	name: string
	phone: string
	state: string[]
	startDate: string
	endDate: string
}

const ITEM_HEIGHT = 48
const ITEM_PADDING_TOP = 8
const MenuProps = {
	PaperProps: {
		style: {
			maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
			width: 250,
		},
	},
}

const states = ['전체', '정상', '주의', '위험']

const HealthData = () => {
	const queryClient = useQueryClient()

	// 	노인 건강 기준:

	// 심박수: 정상 60-100회/분, 주의 50-60 또는 100-110, 위험 <50 또는 >110
	// 수면시간: 정상 7-9시간, 주의 5-7시간 또는 9-10시간, 위험 <5시간 또는 >10시간
	// 걸음수: 정상 >5000보, 주의 3000-5000보, 위험 <3000보

	const columns: GridColDef[] = [
		{
			field: 'id',
			headerName: 'ID',
			width: 70,
		},
		{
			field: 'name',
			headerName: '이름',
			width: 130,
		},
		{
			field: 'phone',
			headerName: '전화번호',
			width: 140,
		},
		{
			field: 'ageSex',
			headerName: '나이/성별',
			width: 100,
		},
		{
			field: 'activivtyStatusNm',
			headerName: '현재 상태',
			width: 130,
			renderCell: (params) => {
				const { warningCount = 0, dangerCount = 0 } = params.row

				if (warningCount === 0 && dangerCount === 0) {
					return (
						<Chip
							label="정상"
							color="success"
						/>
					)
				}
				if (dangerCount > 0 && (warningCount > 0 || warningCount === 0)) {
					return (
						<Chip
							label="위험"
							color="error"
						/>
					)
				}
				if (warningCount > 0 && dangerCount === 0) {
					return (
						<Chip
							label="주의"
							color="warning"
						/>
					)
				}
				return (
					<Chip
						label="알수없음"
						color="default"
					/>
				)
			},
		},
		{
			type: 'number',
			field: 'avgBpm',
			headerName: '최근 심박 (분당)',
			width: 130,
			renderCell: (params) => {
				if (!params.value) return '-'
				const bpm = Math.floor(params.value)
				// 노인 심박수 기준: 정상 60-100, 주의 50-60/100-110, 위험 <50/>110
				let color = '#4caf50' // 정상 (초록)
				let bgColor = '#e8f5e9'
				if (bpm < 50 || bpm > 110) {
					color = '#d32f2f' // 위험 (빨강)
					bgColor = '#ffebee'
				} else if (bpm < 60 || bpm > 100) {
					color = '#ff9800' // 주의 (주황)
					bgColor = '#fff3e0'
				}
				return (
					<Box
						sx={{
							color,
							fontWeight: 'bold',
							backgroundColor: bgColor,
							padding: '4px 8px',
							borderRadius: '4px',
						}}
					>
						{bpm} 회
					</Box>
				)
			},
		},
		{
			type: 'number',
			field: 'totalSleepMin',
			headerName: '최근 수면',
			width: 150,
			renderCell: (params) => {
				if (!params.value) return '-'
				const totalMin = params.value
				const hours = Math.floor(totalMin / 60)
				const mins = Math.floor(totalMin % 60)
				// 노인 수면 기준: 정상 7-9시간, 주의 5-7/9-10시간, 위험 <5/>10시간
				let color = '#4caf50' // 정상 (초록)
				let bgColor = '#e8f5e9'
				if (hours < 5 || hours > 10) {
					color = '#d32f2f' // 위험 (빨강)
					bgColor = '#ffebee'
				} else if (hours < 7 || hours > 9) {
					color = '#ff9800' // 주의 (주황)
					bgColor = '#fff3e0'
				}
				return (
					<Box
						sx={{
							color,
							fontWeight: 'bold',
							backgroundColor: bgColor,
							padding: '4px 8px',
							borderRadius: '4px',
						}}
					>
						{hours} 시간 {mins} 분
					</Box>
				)
			},
		},
		{
			type: 'number',
			field: 'stepCount',
			headerName: '최근 활동량 (걸음수)',
			width: 170,
			renderCell: (params) => {
				if (!params.value) return '-'
				const steps = Math.floor(params.value)
				// 노인 걸음수 기준: 정상 >5000, 주의 3000-5000, 위험 <3000
				let color = '#4caf50' // 정상 (초록)
				let bgColor = '#e8f5e9'
				if (steps < 3000) {
					color = '#d32f2f' // 위험 (빨강)
					bgColor = '#ffebee'
				} else if (steps < 5000) {
					color = '#ff9800' // 주의 (주황)
					bgColor = '#fff3e0'
				}
				return (
					<Box
						sx={{
							color,
							fontWeight: 'bold',
							backgroundColor: bgColor,
							padding: '4px 8px',
							borderRadius: '4px',
						}}
					>
						{steps.toLocaleString()} 걸음
					</Box>
				)
			},
		},
		{
			field: 'lastMeasureDt',
			headerName: '마지막 동기화',
			width: 130,
			renderCell: (params) => {
				return params.value ? params.value : '-'
			},
		},
		{
			field: 'action',
			headerName: '액션',
			width: 120,
			renderCell: (params) => {
				return (
					<Button
						variant="contained"
						size="small"
						fullWidth
						onClick={() => openUserHealthData(params.row.id)}
						startIcon={<ZoomInIcon />}
					>
						보기
					</Button>
				)
			},
		},
	]

	const apiRef = useGridApiRef()
	const [rowSelectionModel, setRowSelectionModel] =
		useState<GridRowSelectionModel>({ type: 'include', ids: new Set() })

	const disabledAdd = true
	const disabledDel = true
	const disabledSave = true
	const [disabledSearch, setDisabledSearch] = useState(false)
	const disabledReset = false
	const [disabledExcelDownload, setDisabledExcelDownload] = useState(false)
	const hideAdd = true
	const hideDel = true
	const hideSave = true
	const hideSearch = false
	const hideReset = false
	const hideExcelDownload = false
	const [loading, setLoading] = useState(false)

	const [popupIsOpen, setPopupIsOpen] = useState(false)
	const [selectedUserId, setSelectedUserId] = useState<number | null>(null)

	// 입력용 state (키 입력시 변경)
	const [searchInput, setSearchInput] = useState<HealthDataSearchType>({
		startDate: dayjs().subtract(7, 'day').format('YYYY-MM-DD'),
		endDate: dayjs().format('YYYY-MM-DD'),
		name: '',
	})

	// 검색용 state (검색 버튼/엔터 시에만 변경)
	const [search, setSearch] = useState<HealthDataSearchType>({
		startDate: dayjs().subtract(7, 'day').format('YYYY-MM-DD'),
		endDate: dayjs().format('YYYY-MM-DD'),
		name: '',
	})

	// React Query를 사용한 게시판 목록 조회
	const {
		data: askData = [],
		isLoading,
		isFetching,
		isError,
		error,
		refetch,
	} = useQuery({
		queryKey: healthDataKeys.list(search),
		queryFn: async () => {
			setLoading(true)
			console.log('apiHealthDataSummaryList 호출 시작 - 검색 파라미터:', search)
			const request: HealthDataSearchType = {
				...search,
			}
			const result = await apiHealthDataSummaryDetail(request).finally(() =>
				setLoading(false),
			)
			console.log('apiHealthDataSummaryList 호출 성공 - 결과:', result)
			return result
		},
		staleTime: 5 * 60 * 1000, // 5분간 캐시 유지
		gcTime: 10 * 60 * 1000, // 10분간 가비지 컬렉션 방지

		// 자동 리페칭 옵션들
		refetchOnWindowFocus: true, // 윈도우 포커스 시 자동 리페칭
		refetchOnReconnect: true, // 네트워크 재연결 시 자동 리페칭
		refetchOnMount: true, // 컴포넌트 마운트 시 자동 리페칭

		// 주기적 리페칭
		refetchInterval: 10 * 60 * 1000, // 30분마다 자동 리페칭
		refetchIntervalInBackground: false, // 백그라운드에서는 리페칭 안함
	})

	// 추가
	const actionAdd = () => {}

	// 삭제
	const actionDel = () => {}

	// 저장
	const actionSave = () => {}

	// 검색
	const actionSearch = () => {
		setSearch({ ...searchInput })
		refetch()
	}

	// 초기화
	const actionReset = () => {
		const initialSearch = {
			startDate: dayjs().subtract(7, 'day').format('YYYY-MM-DD'),
			endDate: dayjs().format('YYYY-MM-DD'),
			name: '',
		}
		setSearchInput(initialSearch)
		setSearch(initialSearch)
	}

	// 키보드 엔터키 처리
	const onKeyDown = (e: React.KeyboardEvent<HTMLFormElement>) => {
		if (e.code === 'Enter' || e.key === 'Enter') {
			e.preventDefault() // 폼 제출 방지
			actionSearch()
		}
	}

	// 폼 제출 방지
	const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault()
	}

	const dialogClose = () => {
		setPopupIsOpen(false)
	}

	// const handleChange = (event: SelectChangeEvent<typeof search.state>) => {
	// 	const {
	// 		target: { value },
	// 	} = event
	// 	setSearch({
	// 		...search,
	// 		state: typeof value === 'string' ? value.split(', ') : value,
	// 	})
	// }

	// 엑셀 다운로드 (action 컬럼 제외)
	const actionExcelDownload = () => {
		if (!apiRef.current) return
		apiRef.current.exportDataAsExcel({
			fields: columns
				.filter((col) => col.field !== 'action' && col.field !== 'id')
				.map((col) => col.field),
			fileName: `건강데이터_${dayjs().format('YYYYMMDD_HHmmss')}`,
		})
	}

	const saveTest = () => {
		const data: HealthDataRequestType[] = [
			{
				crud: 'C',
				heartRateMin: 1,
				heartRateMax: 2,
				bloodPressureSystolic: 3,
				bloodPressureDiastolic: 4,
				bloodOxygenSaturation: 5,
				bloodTemperatureMin: 6.0,
				bloodTemperatureMax: 7.67,
				bodyTemperatureMin: 8.3,
				bodyTemperatureMax: 9.1,
				sleepDeep: 10.44,
				sleepLight: 11.3,
				sleepRem: 12.55,
				activityMax: 13.6,
				activityAvg: 14.333,
				activityWalkCount: 15,
			},
		]
		const request: SpRequestType = {
			cmd: 'SAVE',
			jsonText: JSON.stringify(data),
		}
		apiHealthDataSave(request).then((res) => {
			console.log(res)
		})
	}

	const detailTest = () => {
		const request: HealthDataSearchType = {
			id: 3,
		}
		apiHealthDataList(request).then((res) => console.log(res))
	}

	const openUserHealthData = (id: number) => {
		console.log('선택된 행 데이터:', id)
		if (!id) return
		setSelectedUserId(id)
		setPopupIsOpen(true)
	}

	return (
		<PageContainer>
			<UserData
				dialogOpen={popupIsOpen}
				dialogClose={dialogClose}
				userId={selectedUserId}
			/>
			<PageTop>
				<PagePosition
					actionAdd={actionAdd}
					actionDel={actionDel}
					actionSave={actionSave}
					actionSearch={actionSearch}
					actionReset={actionReset}
					actionExcelDownload={actionExcelDownload}
					disabledAdd={disabledAdd}
					disabledDel={disabledDel}
					disabledSave={disabledSave}
					disabledSearch={disabledSearch}
					disabledReset={disabledReset}
					disabledExcelDownload={disabledExcelDownload}
					hideAdd={hideAdd}
					hideDel={hideDel}
					hideSave={hideSave}
					hideSearch={hideSearch}
					hideReset={hideReset}
					hideExcelDownload={hideExcelDownload}
				/>
				<SearchContainer
					onKeyDown={onKeyDown}
					onSubmit={onSubmit}
				>
					<Grid
						container
						spacing={2}
					>
						<Grid size={{ xs: 12, md: 6 }}>
							<LocalizationProvider
								dateAdapter={AdapterMoment}
								adapterLocale="ko"
							>
								<Box
									sx={{
										display: 'flex',
										gap: 2,
										alignItems: 'center',
										width: '100%',
									}}
								>
									<DatePicker
										label="시작일"
										value={moment(searchInput.startDate)}
										onChange={(newValue) => {
											if (newValue) {
												setSearchInput({
													...searchInput,
													startDate: newValue.format('YYYY-MM-DD'),
												})
											}
										}}
										slotProps={{ textField: { size: 'small' } }}
										sx={{ width: '100%' }}
									/>
									<Typography>~</Typography>
									<DatePicker
										label="종료일"
										value={moment(searchInput.endDate)}
										onChange={(newValue) => {
											if (newValue) {
												setSearchInput({
													...searchInput,
													endDate: newValue.format('YYYY-MM-DD'),
												})
											}
										}}
										slotProps={{ textField: { size: 'small' } }}
										sx={{ width: '100%' }}
									/>
								</Box>
							</LocalizationProvider>
						</Grid>
						<Grid size={{ xs: 12, md: 6 }}>
							<TextField
								label="이름"
								size="small"
								value={searchInput.name}
								onChange={(e) =>
									setSearchInput({ ...searchInput, name: e.target.value })
								}
								fullWidth
							/>
						</Grid>
					</Grid>
				</SearchContainer>
			</PageTop>
			<GridContainer
				sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
			>
				{/* {isError && (
										<Box sx={{ mb: 2 }}>
											<Alert severity="error">
												데이터를 불러오는 중 오류가 발생했습니다: {error?.message}
											</Alert>
										</Box>
									)} */}
				<Box sx={{ flexGrow: 1, minHeight: 0 }}>
					<StyledDataGrid
						apiRef={apiRef}
						columns={columns}
						rows={askData || []}
						showToolbar
						loading={isFetching}
						localeText={koKR.components.MuiDataGrid.defaultProps.localeText}
						initialState={{
							columns: {
								columnVisibilityModel: {
									id: false,
								},
							},
						}}
					/>
				</Box>
				{/* 노인 건강 기준 색상 안내 */}
				<TableContainer
					sx={{
						mt: 1.5,
						flexShrink: 0,
						border: '1px solid #e0e0e0',
						borderRadius: 1,
					}}
				>
					<Table size="small">
						<TableHead>
							<TableRow sx={{ backgroundColor: '#f5f5f5' }}>
								<TableCell
									sx={{ fontWeight: 'bold', textAlign: 'center', width: '25%' }}
								>
									📊 구분
								</TableCell>
								<TableCell
									sx={{
										fontWeight: 'bold',
										textAlign: 'center',
										backgroundColor: '#e8f5e9',
										width: '25%',
									}}
								>
									🟢 정상
								</TableCell>
								<TableCell
									sx={{
										fontWeight: 'bold',
										textAlign: 'center',
										backgroundColor: '#fff3e0',
										width: '25%',
									}}
								>
									🟠 주의
								</TableCell>
								<TableCell
									sx={{
										fontWeight: 'bold',
										textAlign: 'center',
										backgroundColor: '#ffebee',
										width: '25%',
									}}
								>
									🔴 위험
								</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							<TableRow>
								<TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>
									💓 심박수
								</TableCell>
								<TableCell sx={{ textAlign: 'center', backgroundColor: '#e8f5e9' }}>
									60~100회
								</TableCell>
								<TableCell sx={{ textAlign: 'center', backgroundColor: '#fff3e0' }}>
									50~60회 또는 100~110회
								</TableCell>
								<TableCell sx={{ textAlign: 'center', backgroundColor: '#ffebee' }}>
									50회 미만 또는 110회 초과
								</TableCell>
							</TableRow>
							<TableRow>
								<TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>
									😴 수면시간
								</TableCell>
								<TableCell sx={{ textAlign: 'center', backgroundColor: '#e8f5e9' }}>
									7~9시간
								</TableCell>
								<TableCell sx={{ textAlign: 'center', backgroundColor: '#fff3e0' }}>
									5~7시간 또는 9~10시간
								</TableCell>
								<TableCell sx={{ textAlign: 'center', backgroundColor: '#ffebee' }}>
									5시간 미만 또는 10시간 초과
								</TableCell>
							</TableRow>
							<TableRow>
								<TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>
									🚶 걸음수
								</TableCell>
								<TableCell sx={{ textAlign: 'center', backgroundColor: '#e8f5e9' }}>
									5,000보 이상
								</TableCell>
								<TableCell sx={{ textAlign: 'center', backgroundColor: '#fff3e0' }}>
									3,000~5,000보
								</TableCell>
								<TableCell sx={{ textAlign: 'center', backgroundColor: '#ffebee' }}>
									3,000보 미만
								</TableCell>
							</TableRow>
						</TableBody>
					</Table>
				</TableContainer>
			</GridContainer>
		</PageContainer>
	)
}

export default HealthData
